<template>
  <slot></slot>
  <modal :id="modalId" :title="viewModeTitle" @shown="onModalShown">

    <!-- ### Main view mode -->
    <template v-if="mode('main')">

      <div class="form-group row">
        <div class="offset-sm-3 col-sm-9">
          <p>You can add or remove <strong v-text="count"></strong> selected contact(s).</p>
        </div>
      </div>

      <div class="form-group row">
        <label for="sync_claims" class="col-form-label col-sm-3">Claims:</label>

        <div class="col-sm-9">
          <claim-typeahead @claim-selected="addClaim"></claim-typeahead>
        </div>
      </div>

      <div class="form-group row">
        <label for="operation" class="col-form-label col-sm-3">Action:</label>
        <div class="col-sm-9">
          <label class="radio-inline mr-2 mt-1">
            <input type="radio" value="add" v-model="operation"> Add
          </label>
          <label class="radio-inline mt-1">
            <input type="radio" value="remove" v-model="operation"> Remove
          </label>
        </div>
      </div>

      <table class="table table-striped table-hover mt-4 mb-0" v-if="claims.length">
        <thead>
        <tr>
          <th>AAAC Number</th>
          <th>Client</th>
          <th>Claim Status</th>
          <th>Policy Number</th>
          <th>Claim Number</th>
          <th>Insured Name</th>
          <th>Adjuster</th>
          <th>Examiner</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="claim in claims">
          <td>
            <a :href="'/claims/' + claim.id" target="_blank">{{ claim.aaac_number }}</a>
          </td>
          <td>{{ claim.client }}</td>
          <td>{{ claim.status }}</td>
          <td>{{ claim.policy_number }}</td>
          <td>{{ claim.claim_number }}</td>
          <td>{{ claim.insured_name }}</td>
          <td>{{ claim.adjuster }}</td>
          <td>{{ claim.examiner }}</td>
          <td class="text-right">
            <a href="#" class="btn btn-sm btn-outline-secondary py-0" @click.prevent="removeClaim(claim.id)">
              <i class="fa fa-close"></i>
            </a>
          </td>
        </tr>
        </tbody>
      </table>

    </template>

    <!-- ### Confirmation view mode -->
    <template v-if="mode('confirm')">

      <form :id="formId" :action="action" method="POST">

        <div class="text-center">
          <p v-html="confirmMessage"></p>
        </div>

        <slot name="hidden"></slot>
        <input type="hidden" name="_token" :value="$csrf_token">

        <input v-for="contactId in contactIds" type="hidden" name="contacts[]" :value="contactId">
        <input v-for="claimId in claimIds" type="hidden" name="claims[]" :value="claimId">
        <input type="hidden" name="operation" :value="operation">
      </form>

    </template>

    <template #footer>

      <template v-if="mode('main')">
        <button type="button" class="btn btn-default" @click="hideModal">
          <i class="fa fa-fw fa-close"></i> Cancel
        </button>

        <button type="button" class="btn btn-primary"
                @click="setMode('confirm')"
                :disabled="!submitEnable"
                :form="formId">
          <i class="fa fa-fw fa-save"></i> Apply
        </button>
      </template>

      <template v-if="mode('confirm')">
        <button type="button" class="btn btn-default" @click="setMode('main')">
          <i class="fa fa-fw fa-close"></i> Cancel
        </button>

        <button type="submit" class="btn btn-primary" :form="formId">
          <i class="fa fa-refresh"></i> Sync
        </button>
      </template>

    </template>

  </modal>
</template>

<script>
import HasModal from "./../mixins/HasModal";
import {mapState} from 'pinia'
import {useCheckboxListStore} from "../../stores/checkboxList"
import ClaimTypeahead from "../ClaimTypeahead.vue";
import HasViewModes from "../mixins/HasViewModes";

export default {
  components: {ClaimTypeahead},

  /**
   *
   * Modal form allows to sync claim contacts.
   *
   * Input properties:
   *
   * modalId - custom id for modal component.
   * action  - form action URL.
   * title   - form title.
   *
   * Usage examples:
   *
   * <sync-contact-modal modal-id="syncContactModal"
   *                     action="{{ route('contacts.sync-claims') }}"
   *                     title="Contacts Sync">
   *   <a class="btn btn-sm btn-success" href="#" v-modal="'syncContactModal'">
   *     <i class="fa fa-files-o"></i> Add to Claim
   *   </a>
   * </sync-contact-modal>
   *
   */

  mixins: [HasModal, HasViewModes],

  props: {

    // Modal dialog id.
    modalId: {
      required: false,
      type: String,
      default: 'syncContactModal'
    },

    // Form action URL.
    action: {
      type: String,
      default: ""
    },

    // Form title.
    title: {
      type: String,
      default: ""
    },

  },

  /**
   * Variables (models).
   */
  data() {
    return {
      claims: [],
      operation: 'add',
      viewModes: [
        {'mode': 'main', 'title': this.title},
        {'mode': 'confirm', 'title': 'Contacts to Claim(s) Sync Confirmation'},
      ],
    }
  },

  /**
   * Computed values
   */
  computed: {
    ...mapState(useCheckboxListStore, ['checkedIds', 'checkedCount']),

    count() {
      return this.checkedCount();
    },

    formId() {
      return this.modalId + "-form"
    },

    submitEnable() {
      return this.claims?.length > 0;
    },

    contactIds() {
      return this.checkedIds();
    },

    claimIds() {
      return this.claims.map(claim => claim.id);
    },

    mainViewMode() {
      return 'main';
    },

    confirmMessage() {
      return `You are about to ${this.operation} ${this.contactIds.length} selected contact(s) ${this.operation === 'add' ? 'to' : 'from'} ${this.claims.length} selected claim(s)? Are you sure you want to proceed?`;
    },

  },

  /**
   * Functions (methods).
   */
  methods: {

    addClaim(newClaim) {
      let index = this.claims.findIndex(a => a.id === newClaim.id);
      if (index === -1) {
        this.claims.push(newClaim);
      }
    },

    removeClaim(idToRemove) {
      this.claims = this.claims.filter(a => parseInt(a.id) !== parseInt(idToRemove));
    },

    onModalShown() {
      this.setMode(this.mainViewMode);
    },

  },

  mounted() {
    this.viewMode = this.mainViewMode;
  }
}
</script>
